/**
 * A client's one-time codes: the switch, the daily cap, and the pause.
 *
 * Off by default, and off means the client has no trace of it. The client
 * chooses how codes are sent; staff decide whether it may send them, how many a
 * day, and lift a pause fraud detection set — the client cannot, because the
 * point of a pause is that somebody other than the account being abused decides
 * it is safe again.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldAlert } from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  QueryPending,
  ReadOnly,
  Switch,
  api,
  formatDateTime,
  formatMoney,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

import { messagingKeys } from './types';

interface OtpAdmin {
  enabled: boolean;
  daily_cap: number;
  paused_at: string | null;
  paused_reason: string | null;
  configured: boolean;
  today: {
    sent: number;
    delivered: number;
    verified: number;
    verification_rate: string | null;
    cost: string | null;
  };
}

export function OtpCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.otp.edit');
  const url = `/messaging/clients/${clientPublicId}/otp`;
  const key = messagingKeys.otp(clientPublicId);
  const otp = useQuery({ queryKey: key, queryFn: () => api.get<OtpAdmin>(url) });
  const [cap, setCap] = useState('');
  useEffect(() => {
    if (otp.data) setCap(String(otp.data.daily_cap));
  }, [otp.data]);

  const onSaved = (data: OtpAdmin, message: string) => {
    queryClient.setQueryData(key, data);
    toast.success(message);
  };
  const onError = (caught: unknown) =>
    toast.error('Could not change it', caught instanceof ApiError ? caught.message : undefined);

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.put<OtpAdmin>(url, { enabled }),
    onSuccess: (data) => onSaved(data, data.enabled ? `One-time codes on for ${clientName}` : `One-time codes off for ${clientName}`),
    onError,
  });
  const saveCap = useMutation({
    mutationFn: () => api.put<OtpAdmin>(url, { daily_cap: Number(cap) }),
    onSuccess: (data) => onSaved(data, 'Daily cap saved'),
    onError,
  });
  const resume = useMutation({
    mutationFn: () => api.post<OtpAdmin>(`${url}/resume`),
    onSuccess: (data) => onSaved(data, `One-time codes resumed for ${clientName}`),
    onError,
  });

  const data = otp.data;
  const capValid = /^\d{1,7}$/.test(cap.trim());

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <KeyRound className="size-4" />
              One-time codes
            </span>
          }
          description={`Lets ${clientName}'s own app send verification codes to its customers.`}
        />
        <CardBody className="space-y-4">
          {data === undefined ? (
            <QueryPending query={otp} label="Loading one-time codes" />
          ) : (
            <>
              {data.paused_at ? (
                <Alert tone="danger" title="Paused by fraud detection">
                  <div className="space-y-2">
                    <p>
                      {data.paused_reason} Paused {formatDateTime(data.paused_at)}.
                    </p>
                    <p>
                      Resume once {clientName} has protected the form that asks for codes — a CAPTCHA, or a limit per
                      visitor — or it will pause again.
                    </p>
                    {canEdit ? (
                      <Button size="sm" variant="secondary" loading={resume.isPending} onClick={() => resume.mutate()}>
                        <ShieldAlert className="size-3.5" />
                        Resume codes
                      </Button>
                    ) : null}
                  </div>
                </Alert>
              ) : null}
  
              <Switch
                label="One-time codes"
                checked={data.enabled} disabled={toggle.isPending}
                hint={
                  data.enabled
                    ? data.configured
                      ? `${clientName} sends codes with its own OTP template.`
                      : `On, but ${clientName} has not chosen a template and sender yet (Messages → One-time codes).`
                    : `Off: ${clientName} sees nothing of it.`
                }
                onChange={(event) => toggle.mutate(event.target.checked)}
              />
  
              <div className="flex flex-wrap items-end gap-2">
                <Input
                  label="Codes per day"
                  inputMode="numeric"
                  value={cap}
                  containerClassName="w-40"
                  error={capValid ? undefined : 'A whole number.'}
                  hint="Refused past this, until the next business day. Caps the bill if codes are abused."
                  onChange={(event) => setCap(event.target.value)}
                />
                {canEdit && data && cap !== String(data.daily_cap) ? (
                  <Button size="sm" loading={saveCap.isPending} disabled={!capValid} onClick={() => saveCap.mutate()}>
                    Save cap
                  </Button>
                ) : null}
              </div>
  
              <dl className="grid grid-cols-2 gap-3 border-t border-line pt-3 text-sm sm:grid-cols-4">
                <Figure label="Sent today">
                  {formatNumber(data.today.sent)} of {formatNumber(data.daily_cap)}
                </Figure>
                <Figure label="Verified">
                  {data.today.verification_rate === null ? '—' : `${data.today.verification_rate}%`}
                </Figure>
                <Figure label="Delivered">{formatNumber(data.today.delivered)}</Figure>
                <Figure label="Cost today">{formatMoney(data.today.cost)}</Figure>
              </dl>
            </>
          )}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

function Figure({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs text-faint">{label}</dt>
      <dd className="tabular text-body">{children}</dd>
    </div>
  );
}
